/**
 * SalesRepDashboard — own-pipeline metrics for sales_rep role.
 *
 * Shows 4 metric cards: Mis Clientes, Mis Cotizaciones,
 * Cotizaciones Pendientes (draft), Facturacion.
 * Quick links: Nueva Cotizacion, Ver Clientes.
 */

import { Link } from 'react-router-dom'
import type { SalesRepDashboardMetrics } from '@/hooks/useDashboard'
import { formatCents } from './formatters'

interface SalesRepDashboardProps {
  metrics: SalesRepDashboardMetrics
}

export function SalesRepDashboard({ metrics }: SalesRepDashboardProps) {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Mi Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Tu pipeline de ventas — actualizacion cada 5 minutos
        </p>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <p className="text-sm font-medium text-gray-500">Mis Clientes</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {metrics.myClients.toLocaleString('es-CO')}
          </p>
          <p className="mt-1 text-xs text-gray-400">clientes asignados</p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <p className="text-sm font-medium text-gray-500">Mis Cotizaciones</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {metrics.myQuotations.toLocaleString('es-CO')}
          </p>
          <p className="mt-1 text-xs text-gray-400">cotizaciones creadas</p>
        </div>

        <div
          className={`rounded-lg border p-6 shadow-sm ${
            metrics.myPendingQuotations > 0
              ? 'border-yellow-200 bg-yellow-50'
              : 'border-gray-200 bg-white'
          }`}
        >
          <p
            className={`text-sm font-medium ${
              metrics.myPendingQuotations > 0 ? 'text-yellow-700' : 'text-gray-500'
            }`}
          >
            Cotizaciones en Borrador
          </p>
          <p
            className={`mt-2 text-3xl font-bold ${
              metrics.myPendingQuotations > 0 ? 'text-yellow-900' : 'text-gray-900'
            }`}
          >
            {metrics.myPendingQuotations.toLocaleString('es-CO')}
          </p>
          <p
            className={`mt-1 text-xs ${
              metrics.myPendingQuotations > 0 ? 'text-yellow-600' : 'text-gray-400'
            }`}
          >
            {metrics.myPendingQuotations > 0 ? 'requieren atencion' : 'todo al dia'}
          </p>
        </div>

        <div className="rounded-lg border border-green-200 bg-green-50 p-6 shadow-sm">
          <p className="text-sm font-medium text-green-700">Facturacion</p>
          <p className="mt-2 text-3xl font-bold text-green-900">
            {formatCents(metrics.myBillingCents)}
          </p>
          <p className="mt-1 text-xs text-green-600">facturacion mensual de mis clientes</p>
        </div>
      </div>

      {/* Quick links */}
      <div className="mt-8">
        <h2 className="mb-4 text-base font-semibold text-gray-700">Acciones rapidas</h2>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/quotations/new"
            className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Nueva Cotizacion
          </Link>
          <Link
            to="/clients"
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Ver Clientes
          </Link>
        </div>
      </div>
    </div>
  )
}
